import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';

import { Box, Text } from '@components/Restyle';
import AcceptDeclineButtons from '@components/shared/Button/AcceptDeclineButtons';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { Tag } from '@components/shared/Tags/Tag';
import {
  GetTeamDetailDocument,
  ListChatsPaginationDocument,
  ListMessagesDocument,
  Message,
  useListTeamsQuery,
  useUpdateUserRequestStatusMutation,
} from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import { useAppNavigation } from '@navigation/useAppNavigation';

/** Card CTA height — matches the web buttons on the task and project cards. */
const ACTIVITY_CARD_BUTTON_HEIGHT = 48;

/**
 * Badge pill height. Not a theme spacing token — 28 is a control height
 * (`s` padding + the metadata line box), and the scale has no 28 rung.
 */
const ACTIVITY_CARD_BADGE_HEIGHT = 28;

/**
 * Member Activity tag icon box. The fractional value is the design's 16px
 * glyph inset by the tag's padding; `TaskMemberJoinRequest` — the card this
 * one sits beside in the thread — uses the same number, so keep them equal.
 */
const MEMBER_ACTIVITY_TAG_ICON_SIZE = 14.22;

/**
 * Shared shell for the TA-9778 activity card: green badge, emphasized
 * headline, secondary line with the team name highlighted, optional CTA.
 * Presentational only — the two states below differ solely in copy.
 */
const ActivityCard: React.FC<{
  badge: string;
  headline: string;
  /** Omitted when there is no team name to say anything useful about. */
  subline?: React.ReactNode;
  /** Buttons for this state — View Team, or the Approve/Decline row. */
  footer?: React.ReactNode;
}> = ({ badge, headline, subline, footer }) => {
  // 's' (12) separates the copy from the buttons; with no buttons that much
  // trailing space just sits at the bottom of the card, so drop 8px to 'xxs'.
  const lastLineMarginBottom = footer ? 's' : 'xxs';

  return (
    <Box
      borderColor='grey02'
      borderWidth={1}
      borderRadius='xs'
      marginVertical='s'
      backgroundColor='white'
      padding='s'>
      <Box
        mb='s'
        alignSelf='flex-start'
        height={ACTIVITY_CARD_BADGE_HEIGHT}
        backgroundColor='greenSecondaryMild'
        borderRadius='m'
        px='s'
        alignItems='center'
        justifyContent='center'>
        <Text variant='metadataSemiBold' color='textSecondary'>
          {badge}
        </Text>
      </Box>
      <Text
        variant='labelEmphasized'
        color='textPrimary'
        marginBottom={subline ? 'xxs' : lastLineMarginBottom}>
        {headline}
      </Text>
      {!!subline && (
        <Text
          variant='webBodySecondary'
          color='textSecondary'
          marginBottom={lastLineMarginBottom}>
          {subline}
        </Text>
      )}
      {!!footer && (
        <Box flexDirection='row' alignItems='center' alignSelf='flex-start'>
          {footer}
        </Box>
      )}
    </Box>
  );
};

type TeamInvitationCardProps = {
  message: Message;
  filterVal?: string;
  doNotUseGlobalSearch?: boolean;
  /** Scenario 1: user requested to join. Scenario 2: owner invited */
  isOwnerInvited: boolean;
  /** true = current user is owner/admin, false = invited member */
  isOwnerSide: boolean;
  showAcceptDecline?: boolean;
  showJoinTeam?: boolean;
  showViewTeam?: boolean;
};

export const TeamInvitationCard: React.FC<TeamInvitationCardProps> = ({
  message,
  filterVal: _filterVal,
  doNotUseGlobalSearch: _doNotUseGlobalSearch,
  isOwnerInvited,
  isOwnerSide,
  showAcceptDecline,
  showJoinTeam,
  showViewTeam,
}) => {
  const { t } = useTranslation('format');
  const { navigateToMyprofileSubscriptionDetail } = useAppNavigation();
  const { client } = useApolloClient();
  const { userRequest, isSender, body } = message;
  const isPending = userRequest?.status === 'pending';
  const isRejected = userRequest?.status === 'rejected';
  const isOnTheTeam =
    userRequest?.status === 'approved' || userRequest?.status === 'joined';
  const [hasJoined, setHasJoined] = useState(false);
  // "Added to {team}" is a system message: it carries no UserRequest at all,
  // so the event itself is the join.
  const isAddedToTeamBody = !!body && /^Added to\s+.+/i.test(body.trim());

  const teamRequestableId = userRequest?.userRequestableId ?? '';

  // getPublicTeam, not getTeam (TA-10850): this card renders in chat for both
  // sides of a join request, including the requester who is not a member yet —
  // getTeam answers "The requested team could not be found" for them.
  // Also handle cases where `userRequestableType` might be missing but we
  // still have a valid `userRequestableId` for the team.
  //
  // Read the union, not just its `Team` branch (TA-10850 QA): a pending
  // non-member gets the `PublicTeam` branch, and `fullTeam` is undefined for
  // them — which dropped the team name and the View Team target on every
  // legacy message that carries neither a PublicTeam attachment nor a body
  // this card can parse. `name` and `uuid` exist on both branches.
  // withMembers: false (TA-10847) — one query per chat card, and only the
  // name / uuid are read.
  const { team: teamDetail, refetch: refetchTeam } = useTeamDetailFromQuery({
    teamUUId: teamRequestableId,
    includePending: true,
    withMembers: false,
  });

  function getTeamNameFromBody(body: string | null | undefined): string | null {
    if (!body?.trim()) return null;

    const addedToMatch = body.match(/Added to\s+(.+?)\s*$/i);
    if (addedToMatch) return addedToMatch[1].trim();

    const joinedMatch = body.match(/join\s+(.+?)\.\s+This/i);
    if (joinedMatch) return joinedMatch[1].trim();

    // Other patterns where the word "team" is a suffix in the sentence.
    const teamSuffixMatch = body.match(
      /(?:join|from|declined)\s+(.+?)\s+team/i
    );
    if (teamSuffixMatch) return teamSuffixMatch[1].trim();

    return null;
  }

  const parsedTeamName = getTeamNameFromBody(body);

  const { data: listTeamsData } = useListTeamsQuery({
    variables: { includePending: true },
    skip: !!teamRequestableId || !parsedTeamName,
  });

  // TA-10772: getTeam is members-only, so someone still waiting for approval
  // gets nothing back and the card rendered "Ask to join  team". The message
  // now carries the team as a PublicTeam attachment, which every viewer can
  // read — prefer it over parsing the body or querying.
  const publicTeamAttachment = message.attachments?.find(
    (attachment) => attachment?.__typename === 'PublicTeam'
  ) as { name?: string; uuid?: string } | undefined;

  const teamName =
    publicTeamAttachment?.name ?? parsedTeamName ?? teamDetail?.name ?? '';

  const fallbackTeamUuid =
    listTeamsData?.listTeams?.find((team) => {
      if (!teamName) return false;
      return team.name.trim().toLowerCase() === teamName.trim().toLowerCase();
    })?.uuid ?? undefined;

  const teamUuid =
    publicTeamAttachment?.uuid ?? teamDetail?.uuid ?? fallbackTeamUuid;

  const getBodyMessage = (): string => {
    // Special handling for system-like messages such as "Added to {team}"
    // that are treated as team invitations but may not have full
    // `userRequestableType` information.
    if (isAddedToTeamBody && isSender) {
      // Sender sees the raw "Added to {team}" style copy
      return t('models:chat.addedToTeam', { teamName });
    }
    if (isAddedToTeamBody && !isSender) {
      // Non-sender sees "You've been added to {team}"
      return t('models:chat.youHaveBeenAddedToTeam', { teamName });
    }

    if (isOwnerInvited) {
      if (isOwnerSide) {
        if (isPending && !hasJoined)
          return t('models:chat.askToJoinTeam', { teamName });
        if (isRejected) return '';
        return t('models:chat.addedToTeam', { teamName });
      }
      if (isPending && !hasJoined) {
        return t('models:chat.invitedToJoinTeamExpires', { teamName });
      }
      if (isRejected) {
        return t('models:chat.youHaveBeenDeclinedTeam', { teamName });
      }
      return t('models:chat.youHaveBeenAddedToTeam', { teamName });
    }
    if (isOwnerSide) {
      if (isPending && !hasJoined)
        return t('models:chat.requestToJoinTeam', { teamName });
      if (isRejected) return t('models:chat.declinedFromTeam', { teamName });
      return t('models:chat.addedToTeam', { teamName });
    }
    if (isPending && !hasJoined) {
      return t('models:chat.askToJoinTeam', { teamName });
    }
    if (isRejected)
      return t('models:chat.youHaveBeenDeclinedTeam', { teamName });
    return t('models:chat.youHaveBeenAddedToTeam', { teamName });
  };

  const bodyMessage = getBodyMessage();

  // TA-10772: bring the team activity card in line with the TA-9778 card
  // design already used by the task and project cards — once the member is
  // on the team, show the green "MEMBER JOINED" card instead of the flat
  // "Member Activity" tag.
  const memberName = userRequest?.user?.name ?? '';
  const memberFirstName = memberName.split(' ')[0];
  // TA-10772 review: gate on the positive states, not on the absence of a
  // status. Approving or declining evicts the UserRequest from the cache and
  // waits on a listMessages refetch; during that window `userRequest` is
  // undefined, and "not pending and not rejected" made the card assert
  // "MEMBER JOINED" at the admin who had just pressed Decline. Falling
  // through to the legacy card leaves that window neutral, as it was before.
  const showMemberJoinedCard =
    hasJoined || isOnTheTeam || (!userRequest && isAddedToTeamBody);
  const showJoinedByMemberCopy = isOwnerSide && !!memberName;
  // The person who asked to join, looking at their own request.
  const isOwnRequestPending =
    !isOwnerInvited && !isOwnerSide && isPending && !hasJoined;
  // An admin looking at somebody else's request to join.
  const isIncomingRequestPending =
    !isOwnerInvited && isOwnerSide && isPending && !hasJoined;
  // Either side looking at a request that was turned down.
  const isDeclinedRequest = isRejected && !hasJoined;

  const handleViewTeam = () => {
    if (!teamUuid) return;
    client?.refetchQueries({ include: [GetTeamDetailDocument] });
    navigateToMyprofileSubscriptionDetail(teamUuid, undefined);
  };

  const viewTeamButton = teamUuid ? (
    <Button
      borderColor='black'
      backgroundColor='black'
      variant='create'
      isSmall
      width={140}
      height={ACTIVITY_CARD_BUTTON_HEIGHT}
      onPress={handleViewTeam}
      accessibilityLabel={t('models:chat.viewTeam')}>
      <Text variant='labelSmall' color='white'>
        {t('models:chat.viewTeam')}
      </Text>
    </Button>
  ) : undefined;

  const [updateUserRequestStatus] = useUpdateUserRequestStatusMutation({
    fetchPolicy: 'network-only',
    refetchQueries: [
      { query: ListMessagesDocument, variables: { chatId: message.chatId } },
      { query: ListChatsPaginationDocument, variables: { first: 20 } },
    ],
    update: (cache, { data }) => {
      const updated = data?.updateUserRequestStatus;
      if (!updated?.id) return;
      cache.evict({
        id: cache.identify({ __typename: 'UserRequest', id: updated.id }),
      });
      cache.gc();
    },
  });

  const onJoinTeam = () => {
    updateUserRequestStatus({
      variables: {
        id: userRequest?.id ?? '',
        status: 'joined',
      },
      onCompleted: () => {
        setHasJoined(true);
        refetchTeam();
      },
      onError: () => {
        if (teamUuid) {
          navigateToMyprofileSubscriptionDetail(teamUuid, undefined);
        }
      },
    });
  };

  if (showMemberJoinedCard && teamName) {
    return (
      <ActivityCard
        badge={t('models:chat.memberJoinedBadge')}
        headline={
          showJoinedByMemberCopy
            ? t('models:chat.joinedTeam', { name: memberName })
            : bodyMessage
        }
        subline={
          <>
            {showJoinedByMemberCopy
              ? `${t('models:chat.memberJoinedIsNowPartOf', {
                  name: memberFirstName,
                })} `
              : `${t('models:chat.youAreNowPartOf')} `}
            <Text variant='labelEmphasized14' color='greenSecondary'>
              {teamName}
            </Text>
            {` ${t('models:chat.memberJoinedReadyToCollaborate')}`}
          </>
        }
        footer={viewTeamButton}
      />
    );
  }

  // The requester's own pending card — same card design, with the team name
  // moved out of the headline into the highlighted secondary line (TA-10772).
  if (isOwnRequestPending) {
    return (
      <ActivityCard
        badge={t('models:chat.memberActivityBadge')}
        headline={t('models:chat.askToJoinThisTeam')}
        // Without a team name the lead-in would dangle ("You requested to
        // join "), and the headline already says it — so drop the line.
        subline={
          teamName ? (
            <>
              {`${t('models:chat.youRequestedToJoin')} `}
              <Text variant='labelEmphasized14' color='greenSecondary'>
                {teamName}
              </Text>
            </>
          ) : undefined
        }
        footer={viewTeamButton}
      />
    );
  }

  // An admin's view of an incoming request — same card design, with the
  // Approve/Decline controls in the footer slot (TA-10772).
  if (isIncomingRequestPending) {
    return (
      <ActivityCard
        badge={t('models:chat.memberActivityBadge')}
        headline={t('models:chat.requestedToJoinTeam')}
        subline={
          <>
            {memberName
              ? t('models:chat.nameRequestToJoinThisTeam', {
                  name: memberName,
                })
              : bodyMessage}
            {!!teamName && (
              <Text variant='labelEmphasized14' color='greenSecondary'>
                {` ${teamName}`}
              </Text>
            )}
          </>
        }
        footer={
          showAcceptDecline ? (
            <AcceptDeclineButtons
              acceptIconName='CheckCircle'
              suffix={true}
              requestId={userRequest?.id}
              borderRadius='xs'
              height={ACTIVITY_CARD_BUTTON_HEIGHT}
              toggleAcceptDecline={() => {}}
            />
          ) : (
            viewTeamButton
          )
        }
      />
    );
  }

  // A declined request — same card design, no CTA: there is nothing for
  // either side to do from here (TA-10772).
  if (isDeclinedRequest) {
    const declinedOwnerSentence = memberName
      ? t('models:chat.nameDeclinedFromThisTeam', { name: memberName })
      : bodyMessage;

    // The admin's sentence stands alone, so it keeps the subline whether or
    // not a team name resolved. The member's is a lead-in that needs a name
    // after it, so with no name the line is dropped and the headline carries
    // it — same rule as the pending cards.
    let declinedSubline: React.ReactNode;
    if (isOwnerSide) {
      declinedSubline = (
        <>
          {declinedOwnerSentence}
          {!!teamName && (
            <Text variant='labelEmphasized14' color='greenSecondary'>
              {` ${teamName}`}
            </Text>
          )}
        </>
      );
    } else if (teamName) {
      declinedSubline = (
        <>
          {`${t('models:chat.youWereDeclinedFrom')} `}
          <Text variant='labelEmphasized14' color='greenSecondary'>
            {teamName}
          </Text>
        </>
      );
    }

    return (
      <ActivityCard
        badge={t('models:chat.memberActivityBadge')}
        headline={
          isOwnerSide
            ? t('models:chat.declinedFromTeamHeadline')
            : t('models:chat.declinedFromThisTeam')
        }
        subline={declinedSubline}
      />
    );
  }

  return (
    <Box
      borderColor='grey02'
      borderWidth={1}
      borderRadius='xs'
      marginVertical='s'
      backgroundColor='white'>
      <Tag
        label={t('models:chat.memberActivity')}
        labelVariant='metadata'
        variant='joinTask'
        prefixMarginRight='xxs'
        prefix={<Icon name='Users' variant='s' color='magentaDark' />}
        iconWidth={MEMBER_ACTIVITY_TAG_ICON_SIZE}
        iconHeight={MEMBER_ACTIVITY_TAG_ICON_SIZE}
        padding='xs'
        borderTopLeftRadius='xs'
        borderTopRightRadius='xs'
        borderBottomLeftRadius='none'
        borderBottomRightRadius='none'
      />
      {bodyMessage ? (
        <Text
          variant='webBodySecondary'
          color='textSecondary'
          marginHorizontal='xs'
          marginVertical='xs'>
          {bodyMessage}
        </Text>
      ) : null}
      {showAcceptDecline && isPending && (
        <Box
          flexDirection='row'
          alignItems='center'
          marginHorizontal='xs'
          paddingBottom='s'>
          <AcceptDeclineButtons
            acceptIconName='CheckCircle'
            suffix={true}
            requestId={userRequest?.id}
            toggleAcceptDecline={() => {}}
          />
        </Box>
      )}
      {showJoinTeam && isPending && !hasJoined && (
        <Box
          flexDirection='row'
          alignItems='center'
          marginHorizontal='xs'
          paddingVertical='s'>
          <Button
            borderColor='black'
            backgroundColor='black'
            variant='create'
            onPress={onJoinTeam}
            isSmall
            centerIcon={false}
            height={Platform.OS === 'web' ? 32 : 40}
            accessibilityLabel={t('models:chat.joinTeam')}
            prefixMarginRight='xxs'
            prefix={<Icon variant='s' name='CheckCircle' color='white' />}>
            {t('models:chat.joinTeam')}
          </Button>
        </Box>
      )}
      {(showViewTeam || hasJoined) && teamUuid && (
        <Box
          flexDirection='row'
          alignItems='center'
          marginHorizontal='xs'
          paddingBottom='s'>
          <Button
            borderColor='black'
            backgroundColor='white'
            variant='copy'
            onPress={handleViewTeam}
            isSmall
            centerIcon={false}
            height={Platform.OS === 'web' ? 32 : 40}
            paddingHorizontal='l'
            accessibilityLabel={t('models:chat.viewTeam')}>
            {t('models:chat.viewTeam')}
          </Button>
        </Box>
      )}
    </Box>
  );
};
